import "@/App.css";
import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, homeFor, useAuth } from "@/lib/auth";
import type { Role } from "@/lib/types";
import AppShell from "@/components/app/AppShell";
import { Loading } from "@/components/app/PageHeader";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ScreensPage from "@/pages/ScreensPage";
import ScreenDetailPage from "@/pages/ScreenDetailPage";
import GroupsPage from "@/pages/GroupsPage";
import GroupDetailPage from "@/pages/GroupDetailPage";
import MediaPage from "@/pages/MediaPage";
import PlaylistsPage from "@/pages/PlaylistsPage";
import PlaylistEditorPage from "@/pages/PlaylistEditorPage";
import UsersPage from "@/pages/UsersPage";
import ActivityPage from "@/pages/ActivityPage";
import TrainingPage from "@/pages/TrainingPage";
import UpdatesPage from "@/pages/UpdatesPage";
import LandingPage from "@/pages/LandingPage";
import AlertsPage from "@/pages/AlertsPage";
import VideoWallsPage from "@/pages/VideoWallsPage";
import StatsPage from "@/pages/StatsPage";
import ApkAdminPage from "@/pages/admin/ApkAdminPage";
import OrganizationsPage from "@/pages/admin/OrganizationsPage";
import OrganizationDetailPage from "@/pages/admin/OrganizationDetailPage";

function Guard({ roles, children }: { roles?: Role[]; children: React.ReactElement }) {
  const { user } = useAuth();
  if (user === null) return <div className="px-8"><Loading /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <div className="px-8"><Loading /></div>;
  return user ? <Navigate to={homeFor(user)} replace /> : <LandingPage />;
}

const ADMIN: Role[] = ["organization_admin"];
const CONTENT: Role[] = ["organization_admin", "editor"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="/definir-mot-de-passe" element={<SetPasswordPage />} />
          <Route path="/" element={<Home />} />
          <Route element={<Guard><AppShell /></Guard>}>
            <Route path="/tableau-de-bord" element={<Guard roles={ADMIN}><DashboardPage /></Guard>} />
            <Route path="/ecrans" element={<Guard roles={ADMIN}><ScreensPage /></Guard>} />
            <Route path="/ecrans/:id" element={<Guard roles={ADMIN}><ScreenDetailPage /></Guard>} />
            <Route path="/groupes" element={<Guard roles={ADMIN}><GroupsPage /></Guard>} />
            <Route path="/groupes/:id" element={<Guard roles={ADMIN}><GroupDetailPage /></Guard>} />
            <Route path="/medias" element={<Guard roles={CONTENT}><MediaPage /></Guard>} />
            <Route path="/listes" element={<Guard roles={CONTENT}><PlaylistsPage /></Guard>} />
            <Route path="/listes/:id" element={<Guard roles={CONTENT}><PlaylistEditorPage /></Guard>} />
            <Route path="/historique" element={<Guard roles={ADMIN}><ActivityPage /></Guard>} />
            <Route path="/utilisateurs" element={<Guard roles={ADMIN}><UsersPage /></Guard>} />
            <Route path="/formation" element={<TrainingPage />} />
            <Route path="/mises-a-jour" element={<UpdatesPage />} />
            <Route path="/alertes" element={<Guard roles={ADMIN}><AlertsPage /></Guard>} />
            <Route path="/murs-video" element={<Guard roles={ADMIN}><VideoWallsPage /></Guard>} />
            <Route path="/statistiques" element={<Guard roles={ADMIN}><StatsPage /></Guard>} />
            <Route path="/admin/apk" element={<Guard roles={["super_admin"]}><ApkAdminPage /></Guard>} />
            <Route path="/admin/organisations" element={<Guard roles={["super_admin"]}><OrganizationsPage /></Guard>} />
            <Route path="/admin/organisations/:id" element={<Guard roles={["super_admin"]}><OrganizationDetailPage /></Guard>} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" richColors position="top-right" />
    </AuthProvider>
  );
}
